// Explainability page: how the model's suggestions compare to the planners' own
// hand-built stock-order plans, and where the differences genuinely come from.
// Content is a fixed write-up of a one-off benchmark study (six monthly plans,
// March–August 2026) — not a live query.
import type { ReactNode } from 'react'

function Card({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="border border-[#E5E7EC]" style={{ borderTop: '2px solid #92A2BD' }}>
      <div className="border-b border-[#E5E7EC] bg-[#FAFBFC] px-4 py-2.5">
        <span className="text-xs font-bold uppercase tracking-[0.08em] text-[#444]">{title}</span>
        {hint && <span className="ml-2 text-xs normal-case tracking-normal text-[#999]">{hint}</span>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  )
}

function Bar({ label, value, note, tone = 'ocean' }: {
  label: string; value: number; note?: string; tone?: 'ocean' | 'warn' | 'gap'
}) {
  const color = tone === 'warn' ? '#C67C1B' : tone === 'gap' ? '#AE4636' : undefined
  return (
    <div className="grid grid-cols-[110px_1fr_9rem] items-center gap-3">
      <span className="text-[13px] font-bold text-black">{label}</span>
      <span className="relative h-5 overflow-hidden rounded-[2px] bg-[#EEF1F4]">
        <span
          className={`absolute inset-y-0 left-0 rounded-[2px] ${color ? '' : 'bg-bmw-ocean'}`}
          style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%`, background: color }}
        />
      </span>
      <span className="text-right text-xs tabular-nums text-[#666]">
        <b className="text-black">{value.toFixed(2)}</b>{note ? ` · ${note}` : ''}
      </span>
    </div>
  )
}

function Stat({ big, label, tone }: { big: string; label: string; tone?: 'gap' | 'good' }) {
  const c = tone === 'gap' ? 'text-[#AE4636]' : tone === 'good' ? 'text-bmw-ocean' : 'text-black'
  return (
    <div className="border border-[#E5E7EC] p-4" style={{ borderTop: '2px solid #92A2BD' }}>
      <div className={`text-2xl font-bold tabular-nums ${c}`}>{big}</div>
      <div className="mt-1 text-[11px] leading-snug text-[#999]">{label}</div>
    </div>
  )
}

const th = 'px-4 py-2.5 text-left text-[10px] font-bold uppercase tracking-[0.12em] text-bmw-ocean'
const td = 'px-4 py-2.5 border-b border-[#E5E7EC]'
const mono = 'font-mono text-xs'

export function AdminBenchmark() {
  return (
    <div className="max-w-4xl space-y-8">
      <div>
        <h1 className="text-[15px] font-bold uppercase tracking-[0.08em] text-black">Human vs. model benchmark</h1>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-[#999]">
          Six hand-built monthly stock-order plans (March–August 2026) re-run through the predictor on the
          same demand. Comparison covers the 42 model instances with order history (994 cars); the plans
          deliberately target a chosen subset of models, so unmatched models are expected. Agreement is a
          marginal-overlap score: 1.00 = identical mix, 0 = nothing in common.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat big="≈ 0.60" label="Colour & interior agreement — the model captures the popular palette." tone="good" />
        <Stat big="+18%" label="Package agreement gained by comparing at the edition level (naming, not accuracy)." tone="good" />
        <Stat big="0.10" label="Agreement on the exact full build — a combinatorial floor, not a failure." />
      </div>

      <Card title="Agreement by attribute" hint="car-weighted, edition-rolled">
        <div className="space-y-2.5">
          <Bar label="Colour" value={0.63} note="best dimension" />
          <Bar label="Interior" value={0.60} />
          <Bar label="Trim" value={0.51} />
          <Bar label="Package" value={0.49} note="was 0.40 raw" />
          <Bar label="Rim" value={0.44} note="house rule" tone="warn" />
          <Bar label="Full build" value={0.10} note="combinatorial floor" tone="gap" />
        </div>
        <p className="mt-4 text-xs leading-relaxed text-[#666]">
          On any single attribute the model and planners pick from much the same menu (44–63% overlap). Only
          the exact five-way combination is rarely identical — expected, since colour × interior × trim × rim ×
          package has far more combinations than a month's volume can fill.
        </p>
      </Card>

      <Card title="Same car, different name" hint="the biggest apparent gap is naming, not disagreement">
        <p className="text-sm leading-relaxed text-[#444]">
          Planners order at the <b>edition</b> level; the order history stores the fully <b>decoded</b> build.
          An edition contains a base package, which contains a rim:
        </p>
        <div className="my-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="border border-[#CFD3DB] bg-white px-2.5 py-1"><span className={mono}>ZMF</span> <span className="text-[#999]">M Sport Edition — what planners write</span></span>
          <span className="text-[#CFD3DB]">⊃</span>
          <span className="border border-[#CFD3DB] bg-white px-2.5 py-1"><span className={mono}>337</span> <span className="text-[#999]">base package — what the model surfaces</span></span>
          <span className="text-[#CFD3DB]">⊃</span>
          <span className="border border-[#CFD3DB] bg-white px-2.5 py-1"><span className={mono}>1LX</span> <span className="text-[#999]">bundled rim</span></span>
        </div>
        <p className="text-sm leading-relaxed text-[#444]">
          15 old edition codes collapse to just 4 named editions (Active, M Sport, Fully Charged, Supercharged).
          Because <span className={mono}>337</span> sits inside every edition, it is the single most common code in
          the data — so the model reports it, while planners never write it. The model spends <b>28%</b> of its
          package output on the base <span className={mono}>337</span>/<span className={mono}>MET</span> vs the
          planners' <b>4%</b>. Rolling the model's packages up to the edition removes that mismatch and lifts
          package agreement from 0.40 to 0.49 — no re-prediction, just reporting at the level planners work in.
        </p>
      </Card>

      <Card title="What moves agreement — and what doesn't">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b-2 border-bmw-ocean bg-[#FAFBFC]">
              <th className={th}>Lever</th><th className={th}>Effect</th><th className={th}>Verdict</th>
            </tr></thead>
            <tbody>
              <tr><td className={td}>90/10 BTS ↑ (vs 70/30 BTO)</td><td className={td}>config 0.078 → 0.076</td><td className={`${td} text-[#AE4636]`}>no change</td></tr>
              <tr><td className={td}>Recency half-life 30d</td><td className={td}>colour +0.04, package −0.02</td><td className={`${td} text-[#C67C1B]`}>mixed</td></tr>
              <tr><td className={td}>Loosen tail cutoffs (min-row 1%, coverage 95%)</td><td className={td}>marginal mean 0.498 → 0.511</td><td className={`${td} text-bmw-ocean`}>small real win</td></tr>
              <tr><td className="px-4 py-2.5">Option-cluster / block caps</td><td className="px-4 py-2.5">changes block count, not agreement</td><td className="px-4 py-2.5 text-[#666]">decoupled</td></tr>
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-[#666]">
          Re-weighting the data doesn't close the gap: the planners' colour mix is no closer to past
          <b> BTS</b> orders (0.66) than to past <b>BTO</b> orders (0.67) — the two channels have nearly the same
          palette. The model faithfully preserves that history (0.74). The residual is planners re-ranking the
          <em> same</em> colours for the season ahead — forward judgment that isn't in any historical data.
        </p>
      </Card>

      <Card title="What the difference really is" hint="three parts, none a tuning failure">
        <ol className="space-y-3">
          {[
            ['Naming', 'Edition vs decoded package — cosmetic. Handled by comparing at the edition level.'],
            ['Forward judgment', 'Planners re-rank the same colour palette and set a house rim/paint for the season ahead (e.g. a stock rim absent from customer history). Not derivable from past orders — a call only a planner makes.'],
            ['Style', 'Planners build ~2 compact blocks per model per month; the model surfaces ~6–9 granular ones. Different, not worse — and the editable preview lets planners collapse toward their style when they want.'],
          ].map(([h, b], i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-0.5 font-mono text-xs font-bold text-bmw-ocean">{String(i + 1).padStart(2, '0')}</span>
              <span className="text-sm leading-relaxed text-[#444]"><b className="text-black">{h}.</b> {b}</span>
            </li>
          ))}
        </ol>
        <p className="mt-4 border-t border-[#E5E7EC] pt-3 text-xs leading-relaxed text-[#666]">
          After those three, what remains is the ~0.10 combinatorial floor. The model is doing its job — the gap
          is mostly things a data-driven model shouldn't be expected to reproduce.
        </p>
      </Card>

      <Card title="Experiment — does baking in human expertise help?" hint="tested on the validation oracle">
        <p className="text-sm leading-relaxed text-[#444]">
          To settle whether the planners know something the model doesn't, we biased the predictor toward the
          planners' preferences: for each rolling-origin fold we blended the model's per-attribute mix toward the
          human prior by a factor λ, then scored it against the <b>real future customer (BTO) batch</b> — the same
          oracle as the Data benchmark. λ=0 is the untouched model; λ=1 is the pure human prior.
          <span className="text-[#999]"> (7 models with plans, 1,727 test cars, B=50; accuracy = 1 − total-variation distance.)</span>
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead><tr className="border-b-2 border-bmw-ocean bg-[#FAFBFC]">
              <th className={th}>Attribute</th>
              <th className={`${th} text-right`}>Model (λ0)</th>
              <th className={`${th} text-right`}>Blend λ0.5</th>
              <th className={`${th} text-right`}>Pure human (λ1)</th>
              <th className={`${th} text-right`}>Floor</th>
            </tr></thead>
            <tbody className="tabular-nums">
              {[
                ['Colour', '70.6', '66.5', '49.0', '77.7'],
                ['Interior', '79.4', '66.4', '38.8', '82.9'],
                ['Rim', '80.1', '71.7', '54.6', '82.5'],
              ].map(([a, m, h5, h1, f]) => (
                <tr key={a}>
                  <td className={td}>{a}</td>
                  <td className={`${td} text-right font-bold text-bmw-ocean`}>{m}%</td>
                  <td className={`${td} text-right text-[#666]`}>{h5}%</td>
                  <td className={`${td} text-right text-[#AE4636]`}>{h1}%</td>
                  <td className={`${td} text-right text-[#999]`}>{f}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm leading-relaxed text-[#444]">
          <b>Every attribute is best at λ=0 — the untouched model.</b> Adding human bias makes accuracy
          monotonically <em>worse</em>, and the pure human prior lands 30–40 points below the model. The model
          already sits within ~3 points of the floor (the achievable sampling ceiling), so there's little headroom
          for human input to fill.
        </p>
        <p className="mt-3 border-t border-[#E5E7EC] pt-3 text-xs leading-relaxed text-[#666]">
          This is the strongest evidence for why the model differs from the planners: <b className="text-black">it's
          a better estimate of what customers actually order.</b> The divergence isn't model error — it's the
          planners deliberately merchandising stock (forward-planning, house rims, editions), which is a different
          objective from tracking demand. Worth stating: the oracle is customer (BTO) demand, so this shows human
          bias doesn't improve <em>demand</em> prediction — not that the expertise is worthless. Its right home is
          the editable preview, applied on top of the model, not baked inside it.
        </p>
      </Card>

      <p className="text-[11px] leading-relaxed text-[#999]">
        Method: plans parsed from Specs_AUG_08072026_final.xlsx; predictor run per model at the planned car count
        using production defaults (70/30 BTO, 180-day recency). Edition hierarchy from the package-contents and
        NMC tables. Some matched models are thin (e.g. M340i, 3 historical orders) and noisier. Marginal overlap,
        not a car-for-car match. The human-prior experiment (engine/human_prior_experiment.py) blends the model's
        per-dimension mix toward the plans' colour/interior/rim prior over the same rolling-origin folds as the
        Data benchmark.
      </p>
    </div>
  )
}
